import React, { useContext, useEffect, useState } from 'react'
import { DemoAccountDetails, UpdateDemoTradingAccount } from 'api/Clients/Types/DemoAccountDetails'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { Loading } from 'components/Loading'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchAccountGroupByDemoAccount,
  fetchTradingAccountsLeverageTypesAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { isEmpty } from 'utils/is-empty'

import { DemoTradingAccountNavigation } from '../Navigation/DemoTradingAccountNavigation'
import { TradingAccountTab } from '../Navigation/TradingAccountNavigation'
import {
  AccountFundsType,
  DemoWelcomeAccountDetailsForm,
  isAddFunds,
} from './Form/DemoWelcomeAccountDetailsForm'
import {
  FundsTradingDemoWelcomeAccountForm,
  FundsTradingDemoWelcomeAccountFormValues,
} from './Form/FundsTradingDemoWelcomeAccountForm'
import TradingAccountDetailsPage, { TradingAccountDetailsProps } from './TradingAccountDetailsPage'

interface Props extends TradingAccountDetailsProps {
  data: DemoAccountDetails
  forceRefresh(): void
}

const DemoAccountDetailsPage: React.FC<Props> = ({
  data,
  isEdit,
  setEdit,
  forceRefresh,
  ...props
}) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const dispatch = useAppDispatch()

  const setToast = useContext(ToastContext)

  const [accountFundsTypeModal, setAccountFundsTypeModal] = useState<AccountFundsType>()
  const [leverageModalConfirmation, setLeverageModalConfirmation] = useState<{
    visible: boolean
    values?: UpdateDemoTradingAccount
  }>({ visible: false })

  const leverageType = useAppSelector((state) => state.clients.leverageType)
  const accountGroupsByDemoAccount = useAppSelector(
    (state) => state.tradingAccounts.accountGroupsByDemoAccount
  )

  useEffect(() => {
    if (data) {
      dispatch(fetchAccountGroupByDemoAccount(tradingAccountsApiClient, data.id))
      if (isEmpty(accountGroupsByDemoAccount)) {
        dispatch(fetchTradingAccountsLeverageTypesAction(tradingAccountsApiClient))
      }
    }
  }, [data.id, data]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenAddDeductFunds = async (fundsType: AccountFundsType) => {
    setAccountFundsTypeModal(fundsType)
  }

  const onSubmitWithMaybeConfirmation = async (values: UpdateDemoTradingAccount) => {
    const isChangingLeverage = data.leverage.id !== values.leverageTypeId
    if (isChangingLeverage) {
      const isLeverageEnabled = leverageType.find(
        (l) => l.id === Number(values.leverageTypeId)
      )?.enabled
      if (!isLeverageEnabled) {
        return setLeverageModalConfirmation({ visible: true, values })
      } else {
        await onSubmit(values)
      }
    } else {
      await onSubmit(values)
    }
  }

  const onSubmit = async (values: UpdateDemoTradingAccount) => {
    if (data) {
      try {
        await tradingAccountsApiClient.updateDemoAccount(data.id, values)
        setToast(successToast('Demo Account updated.'))
        forceRefresh()
      } catch (e) {
        console.error(e)
        setLeverageModalConfirmation({ visible: false, values: undefined })
        forceRefresh()
      }
    }
  }

  const onSubmitAddDeductFunds = async (values: FundsTradingDemoWelcomeAccountFormValues) => {
    if (data && accountFundsTypeModal) {
      await tradingAccountsApiClient.depositWithdrawalTradingAccount(
        'demo-accounts',
        accountFundsTypeModal,
        values
      )
      setToast(
        successToast(
          `Funds ${isAddFunds(accountFundsTypeModal) ? 'deposited' : 'withdrawn'} successfully.`
        )
      )
      setAccountFundsTypeModal(undefined)
      forceRefresh()
    }
  }

  return (
    <React.Fragment>
      {leverageModalConfirmation.visible && leverageModalConfirmation.values && (
        <ConfirmationModal
          contentText={`You have requested to change the Leverage to an option we no longer offer in Client Area/Website.
                        Please confirm your action.`}
          yesButtonText='Proceed'
          onYes={() => onSubmit(leverageModalConfirmation.values as UpdateDemoTradingAccount)}
          onCancel={() =>
            setLeverageModalConfirmation({
              visible: false,
              values: undefined,
            })
          }
          visible={leverageModalConfirmation.visible}
        />
      )}
      <DemoTradingAccountNavigation
        data={data}
        hideTabs={data.accountGroup.platformType.id === 3}
        isEdit={isEdit}
        tab={TradingAccountTab.Details}
      />
      <Loading isLoading={!data}>
        {data && (
          <React.Fragment>
            <DemoWelcomeAccountDetailsForm
              {...props}
              data={data}
              isEdit={isEdit}
              setEdit={setEdit}
              onSubmit={onSubmitWithMaybeConfirmation}
              onOpenAddDeductFunds={handleOpenAddDeductFunds}
              accountGroups={accountGroupsByDemoAccount}
              leverageType={leverageType}
              canEditTradingAccount={PermissionsEnum.CanEditDemoTradingAccount}
              canResetAccountPassword={PermissionsEnum.CanResetDemoAccountPassword}
            />
          </React.Fragment>
        )}
      </Loading>
      {accountFundsTypeModal && data && (
        <Modal
          width={600}
          padding='3rem'
          visible={!!accountFundsTypeModal}
          onClose={() => setAccountFundsTypeModal(undefined)}
        >
          <FundsTradingDemoWelcomeAccountForm
            fundType={accountFundsTypeModal}
            data={data}
            onSubmit={onSubmitAddDeductFunds}
          />
        </Modal>
      )}
    </React.Fragment>
  )
}

interface DemoTradingAccountDetailsProps {
  data: DemoAccountDetails
  forceRefresh(): void
}

const DemoTradingAccountDetailsPageComponent: React.FC<DemoTradingAccountDetailsProps> = (
  props
) => {
  const { data, forceRefresh } = props
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  return (
    <TradingAccountDetailsPage
      data={data}
      type='demoAccount'
      resetTradingAccountPassword={tradingAccountsApiClient.resetDemoTradingAccountPassword}
      render={(props) => (
        <DemoAccountDetailsPage {...props} data={data} forceRefresh={forceRefresh} />
      )}
    />
  )
}

export default DemoTradingAccountDetailsPageComponent
